import axios, { type AxiosInstance } from 'axios';
import { action, computed, flow, makeObservable, observable, runInAction } from 'mobx';

import type { Wellbore } from '../../../shared/api/IDashboardService';
import type { BaseParameter, IWellDirectoryService, WellLog } from '../../../shared/api/IWellDirectoryService';
import type { BaseParameterTypes } from '../../../shared/baseParams';
import { EventEmitter } from '../../../shared/EventEmitter';
import type { IDirectoriesService, TDictObject } from '../../../shared/IDirectoriesService';
import type { INotificationsService } from '../../../shared/INotificationsService';
import type { Wellbores } from '../../../shared/stores/WellboresStore';
import type { TUserData } from '../../../shared/types';
import { DRILLING_LOADS_MNEMONICS } from '../../constants';
import type { SettingsStore } from '../../stores/SettingsStore';
import type { TRawAlertRule, TAlertRule, TRawAlertRulesRequestDto, TIndexType } from '../../types';

import type { TData } from './AlertRule.entity';
import { AlertRule } from './AlertRule.entity';
import { AlertRulesApi } from './AlertRulesApi';
import { AlertRulesNotificationsApi, type TNotificationStatus } from './AlertRulesNotificationsApi';
import { AlertRulesTableUiState } from './AlertRulesTableUiState';
import type {
  AlertRulesTemplatesManager,
  TAlertRulesTemplateData,
  TAlertRuleTemplateData,
} from './AlertRulesTemplatesManager';
import { AlertRulesValidationService } from './AlertRulesValidationService';

type TEventMap = {
  newAlertRules: AlertRule[];
  deleteAlertRules: string[];
  updateAlertRules: AlertRule[];
};

const mapSourceOption = (sourceData: TRawAlertRule['sourceData']): TAlertRule['sourceOption'] => {
  switch (sourceData.type) {
    case 'GENERAL_PARAMETERS':
      return sourceData.param;
    case 'DRILLING_LOADS':
      return sourceData.mnemonic;
    case 'AUTOSUPERVISOR_OPERATIONS':
    case 'AUTOSUPERVISOR_KPI_STATES':
      return sourceData.code;
    default:
      sourceData satisfies never;
      return undefined;
  }
};

const serializeSourceData = (
  sourceType: TAlertRule['sourceType'],
  sourceOption: NonNullable<TAlertRule['sourceOption']>,
) => {
  switch (sourceType) {
    case 'GENERAL_PARAMETERS':
      return {
        type: sourceType,
        param: sourceOption as BaseParameterTypes,
      };
    case 'DRILLING_LOADS':
      return {
        type: sourceType,
        mnemonic: String(sourceOption),
      };
    case 'AUTOSUPERVISOR_OPERATIONS':
    case 'AUTOSUPERVISOR_KPI_STATES':
      return {
        type: sourceType,
        code: Number(sourceOption),
      };
    default:
      sourceType satisfies never;
      throw new Error(`Unsupported source type: ${String(sourceType)}`);
  }
};

const mapAlertRule = (ruleRaw: TRawAlertRule): TAlertRule => {
  const mappedRule: TAlertRule = {
    id: ruleRaw.id,
    indexType: ruleRaw.indexType,
    wellboreId: ruleRaw.wellboreId,
    journalEnabled: ruleRaw.journalEnabled,
    riskLevel: ruleRaw.riskLevel,
    anomalyTypeId: ruleRaw.anomalyTypeId ?? undefined,
    anomalySubtypeId: ruleRaw.anomalySubtypeId ?? undefined,
    sourceType: ruleRaw.sourceData.type,
    sourceOption: mapSourceOption(ruleRaw.sourceData),
    holeDepthFrom: ruleRaw.holeDepthIntervalFrom ?? undefined,
    holeDepthTo: ruleRaw.holeDepthIntervalTo ?? undefined,
    holeDepthUnitId: 0,
    depthFrom: ruleRaw.bitDepthIntervalFrom ?? undefined,
    depthTo: ruleRaw.bitDepthIntervalTo ?? undefined,
    depthUnitId: 0,
    unitId: ruleRaw.unitId,
    minValue: ruleRaw.comparativeMethod?.minimumLimit ?? undefined,
    minGoStreamCurveId: ruleRaw.comparativeMethod?.minimumReferenceGoStreamCurveId ?? undefined,
    minCommonParameterId: ruleRaw.comparativeMethod?.minimumReferenceCommonParameterId ?? undefined,
    minPercent: ruleRaw.comparativeMethod?.acceptableMinimumLimit ?? undefined,
    maxValue: ruleRaw.comparativeMethod?.maximumLimit ?? undefined,
    maxGoStreamCurveId: ruleRaw.comparativeMethod?.maximumReferenceGoStreamCurveId ?? undefined,
    maxCommonParameterId: ruleRaw.comparativeMethod?.maximumReferenceCommonParameterId ?? undefined,
    maxPercent: ruleRaw.comparativeMethod?.acceptableMaximumLimit ?? undefined,
    diffInterval: ruleRaw.comparativeMethod?.periodMinutes ?? undefined,
    diffValue: ruleRaw.comparativeMethod?.delta ?? undefined,
    stageIds: ruleRaw.context?.stageIds ?? [],
    wellboreTypeIds: ruleRaw.context?.wellboreTypeInstanceIds ?? [],
    trajectoryTypeIds: ruleRaw.context?.trajectoryTypeIds ?? [],
    sectionTypeIds: ruleRaw.context?.sectionTypeIds ?? [],
    stratUnitTypeIds: ruleRaw.context?.stratUnitTypeIds ?? [],
    reservoirIds: ruleRaw.context?.reservoirIds ?? [],
    runTypeIds: ruleRaw.context?.runTypeIds ?? [],
    holeTypes: ruleRaw.context?.holeTypes ?? [],
    operationTypeIds: ruleRaw.context?.operationTypeIds ?? [],
    createdBy: ruleRaw.createdBy,
    updatedBy: ruleRaw.updatedBy,
    createdAt: ruleRaw.createdAt,
    updatedAt: ruleRaw.updatedAt,
  };

  return mappedRule;
};

const serializeAlertRule = (
  alertRule: AlertRule,
  indexType: TIndexType,
  wellId: number,
  wellboreId: number,
): TRawAlertRulesRequestDto => {
  if (alertRule.data.unitId == null) {
    throw new Error('Unit id is required');
  }

  if (alertRule.data.sourceType == null) {
    throw new Error('Source type is required');
  }

  if (alertRule.data.sourceOption == null) {
    throw new Error('Parameter id is required');
  }

  const sourceData = serializeSourceData(alertRule.data.sourceType, alertRule.data.sourceOption);

  return {
    id: alertRule.id ?? undefined,
    wellId,
    wellboreId,
    indexType,
    journalEnabled: alertRule.data.journalEnabled ?? false,
    riskLevel: alertRule.data.riskLevel,
    anomalyTypeId: alertRule.data.anomalyTypeId,
    anomalySubtypeId: alertRule.data.anomalySubtypeId,
    sourceData,
    holeDepthIntervalFrom: alertRule.data.holeDepthFrom ?? null,
    holeDepthIntervalTo: alertRule.data.holeDepthTo ?? null,
    bitDepthIntervalFrom: alertRule.data.depthFrom ?? null,
    bitDepthIntervalTo: alertRule.data.depthTo ?? null,
    unitId: alertRule.data.unitId,
    comparativeMethod: {
      minimumLimit: alertRule.data.minValue ?? null,
      minimumReferenceGoStreamCurveId: alertRule.data.minGoStreamCurveId ?? null,
      minimumReferenceCommonParameterId: alertRule.data.minCommonParameterId ?? null,
      acceptableMinimumLimit: alertRule.data.minPercent ?? null,
      maximumLimit: alertRule.data.maxValue ?? null,
      maximumReferenceGoStreamCurveId: alertRule.data.maxGoStreamCurveId ?? null,
      maximumReferenceCommonParameterId: alertRule.data.maxCommonParameterId ?? null,
      acceptableMaximumLimit: alertRule.data.maxPercent ?? null,
      delta: alertRule.data.diffValue ?? null,
      periodMinutes: alertRule.data.diffInterval ?? null,
    },
    context: {
      stageIds: alertRule.data.stageIds ?? [],
      wellboreTypeInstanceIds: alertRule.data.wellboreTypeIds ?? [],
      trajectoryTypeIds: alertRule.data.trajectoryTypeIds ?? [],
      sectionTypeIds: alertRule.data.sectionTypeIds ?? [],
      stratUnitTypeIds: alertRule.data.stratUnitTypeIds ?? [],
      reservoirIds: alertRule.data.reservoirIds ?? [],
      runTypeIds: alertRule.data.runTypeIds ?? [],
      operationTypeIds: alertRule.data.operationTypeIds ?? [],
      holeTypes: alertRule.data.holeTypes ?? [],
    },
  };
};

export class AlertRulesService {
  private readonly api: AlertRulesApi;
  private readonly notificationsApi: AlertRulesNotificationsApi;
  private readonly abortControllers = new Map<string, AbortController>();

  readonly alertRulesEventEmitter = new EventEmitter<TEventMap>();
  readonly validationService = new AlertRulesValidationService();
  readonly uiState = new AlertRulesTableUiState();

  @observable.ref baseParameters: BaseParameter[] = [];
  @observable.ref wellboreLogs: WellLog[] = [];
  @observable.ref anomalyTypes: TDictObject[] = [];
  @observable.ref anomalySubtypes: TDictObject[] = [];
  @observable.ref parameterTypes: TDictObject[] = [];
  @observable.ref wellboreTypes: TDictObject[] = [];
  @observable.ref trajectoryTypes: TDictObject[] = [];
  @observable.ref sectionTypes: TDictObject[] = [];
  @observable.ref stratUnitTypes: TDictObject[] = [];
  @observable.ref reservoirTypes: TDictObject[] = [];
  @observable.ref runTypes: TDictObject[] = [];
  @observable.ref unitsList: TDictObject[] = [];
  @observable.ref workClassifierStageTypes: TDictObject[] = [];
  @observable.ref classOfUnits: TDictObject[] = [];
  @observable.ref autosupervisorOperations: TDictObject[] = [];
  @observable.ref autosupervisorKPIstates: TDictObject[] = [];

  @observable loading = false;
  @observable areDirectoriesLoaded = false;

  @observable private alertRules: AlertRule[] = [];
  @observable deletedAlertRulesIds: string[] = [];

  alertRulesMap = observable.map<string, AlertRule>();

  @observable isTemplatesDrawerOpen = false;

  constructor(
    private readonly wellId: number,
    public readonly index: 'time' | 'depth',
    agent: AxiosInstance,
    private readonly notifications: INotificationsService,
    private readonly directoryService: IDirectoriesService,
    private readonly wellDirectoryService: IWellDirectoryService,
    private readonly userData: TUserData,
    public readonly settingsStore: SettingsStore,
    public readonly wellbores: Wellbores,
    public readonly alertRulesTemplatesManager: AlertRulesTemplatesManager,
  ) {
    this.api = new AlertRulesApi(agent);
    this.notificationsApi = new AlertRulesNotificationsApi(agent);

    makeObservable(this);
  }

  private serializeAlertRules = (alertRules: AlertRule[], wellboreId: number): TRawAlertRulesRequestDto[] => {
    return alertRules.map((alertRule) =>
      serializeAlertRule(alertRule, this.index === 'time' ? 'LOG_TIME' : 'LOG_DEPTH', this.wellId, wellboreId),
    );
  };

  private getDefaultAlertRuleData(): TData {
    return {
      journalEnabled: true,
      riskLevel: 'MED_RISK',
      sourceType: 'GENERAL_PARAMETERS',
      indexType: this.index === 'time' ? 'LOG_TIME' : 'LOG_DEPTH',
    };
  }

  private getBaseParametersByIndexType(indexType: TIndexType): BaseParameter[] {
    return this.baseParameters.filter((parameter) => parameter.logType === indexType);
  }

  private getUnitIdByBaseParameter(sourceOption: TData['sourceOption']): number | undefined {
    const baseParameter =
      this.index === 'time'
        ? this.getBaseParametersByIndexType('LOG_TIME').find((parameter) => parameter.parameter === sourceOption)
        : this.getBaseParametersByIndexType('LOG_DEPTH').find((parameter) => parameter.parameter === sourceOption);
    const curve = this.wellboreLogs
      .flatMap((log) => log.curves)
      .find((curve) => curve.goStreamId === baseParameter?.curveId);

    return curve?.unit.id;
  }

  private getAllowedUnitIds(data: Partial<TData>): number[] | null {
    if (data.sourceType === 'AUTOSUPERVISOR_OPERATIONS' || data.sourceType === 'AUTOSUPERVISOR_KPI_STATES') {
      return this.unitsList
        .filter((option) => option.data.abbreviation === 's' || option.data.abbreviation === 'min')
        .map((option) => Number(option.id));
    }

    if (data.sourceType === 'GENERAL_PARAMETERS') {
      const baseParameter = this.baseParameters.find((parameter) => parameter.parameter === data.sourceOption);
      const curve = this.wellboreLogs
        .flatMap((log) => log.curves)
        .find((curve) => curve.goStreamId === baseParameter?.curveId);
      const classOfUnitId = curve?.parameter.classOfUnitId;
      const classOfUnit = this.classOfUnits.find((unitClass) => unitClass.id === classOfUnitId);
      const unitIds = classOfUnit?.data['unitIds'];

      return Array.isArray(unitIds) ? unitIds.filter((unitId): unitId is number => typeof unitId === 'number') : null;
    }

    return null;
  }

  private isUnitAllowed(data: Partial<TData>, unitId: TData['unitId']): boolean {
    if (unitId == null) {
      return true;
    }

    const allowedUnitIds = this.getAllowedUnitIds(data);

    return allowedUnitIds == null || allowedUnitIds.includes(unitId);
  }

  private getAnomalyTypeIdBySubtype(subtypeId: TData['anomalySubtypeId']): number | undefined {
    const anomalyTypeId = this.anomalySubtypes.find((subtype) => subtype.id === subtypeId)?.data['anomalyType'];

    return typeof anomalyTypeId === 'number' ? anomalyTypeId : undefined;
  }

  private getSourceTypeBySourceOption(sourceOption: TData['sourceOption']): TData['sourceType'] | undefined {
    if (sourceOption == null) {
      return undefined;
    }

    if (typeof sourceOption === 'string') {
      if ((DRILLING_LOADS_MNEMONICS as readonly string[]).includes(sourceOption)) {
        return 'DRILLING_LOADS';
      }

      return 'GENERAL_PARAMETERS';
    }

    const matchedTypes: TData['sourceType'][] = [];

    if (this.autosupervisorOperations.some((operation) => operation.data.code === sourceOption)) {
      matchedTypes.push('AUTOSUPERVISOR_OPERATIONS');
    }

    if (this.autosupervisorKPIstates.some((kpi) => kpi.data.code === sourceOption)) {
      matchedTypes.push('AUTOSUPERVISOR_KPI_STATES');
    }

    return matchedTypes.length === 1 ? matchedTypes[0] : undefined;
  }

  private normalizeAlertRulePatch(baseData: TData, patch: Partial<TData>): Partial<TData> {
    const nextData = { ...baseData, ...patch };
    const normalizedPatch: Partial<TData> = { ...patch };

    if ('anomalyTypeId' in patch && !('anomalySubtypeId' in patch)) {
      normalizedPatch.anomalySubtypeId = undefined;
    }

    if ('anomalySubtypeId' in patch && patch.anomalySubtypeId != null && !('anomalyTypeId' in patch)) {
      const anomalyTypeId = this.getAnomalyTypeIdBySubtype(patch.anomalySubtypeId);

      if (anomalyTypeId != null) {
        normalizedPatch.anomalyTypeId = anomalyTypeId;
      }
    }

    if ('sourceOption' in patch && patch.sourceOption != null && !('sourceType' in patch)) {
      const sourceType = this.getSourceTypeBySourceOption(patch.sourceOption);

      if (sourceType != null) {
        normalizedPatch.sourceType = sourceType;
      }
    }

    if ('sourceOption' in patch && patch.sourceOption == null) {
      normalizedPatch.unitId = undefined;
    }

    if ('sourceType' in patch) {
      normalizedPatch.minGoStreamCurveId = undefined;
      normalizedPatch.maxGoStreamCurveId = undefined;

      if (!('sourceOption' in patch)) {
        normalizedPatch.sourceOption = undefined;
      }

      normalizedPatch.unitId = undefined;
    }

    const effectiveData = { ...nextData, ...normalizedPatch };

    if (effectiveData.sourceType === 'GENERAL_PARAMETERS' && 'sourceOption' in patch) {
      const unitId = this.getUnitIdByBaseParameter(patch.sourceOption);

      normalizedPatch.unitId = unitId;
    }

    if ('minValue' in patch && patch.minValue != null) {
      normalizedPatch.minGoStreamCurveId = undefined;
    }

    if ('minGoStreamCurveId' in patch && patch.minGoStreamCurveId != null) {
      normalizedPatch.minValue = undefined;
    }

    if ('maxValue' in patch && patch.maxValue != null) {
      normalizedPatch.maxGoStreamCurveId = undefined;
    }

    if ('maxGoStreamCurveId' in patch && patch.maxGoStreamCurveId != null) {
      normalizedPatch.maxValue = undefined;
    }

    if ('unitId' in normalizedPatch && !this.isUnitAllowed(effectiveData, normalizedPatch.unitId)) {
      normalizedPatch.unitId = undefined;
    }

    return normalizedPatch;
  }

  private createAlertRuleDataFromPatch(patch: Partial<TData>): TData {
    const baseData = this.getDefaultAlertRuleData();
    const normalizedPatch = this.normalizeAlertRulePatch(baseData, patch);

    return {
      ...baseData,
      ...normalizedPatch,
    };
  }

  @flow.bound
  private async *fetchAlertRules(wellboreId: number) {
    this.loading = true;

    this.abortControllers.get('fetchAlertRules')?.abort();

    const newAbortController = new AbortController();
    this.abortControllers.set('fetchAlertRules', newAbortController);

    try {
      const alertRules = await this.api.fetchAlertRules(
        { wellboreId, indexType: this.index === 'time' ? 'LOG_TIME' : 'LOG_DEPTH' },
        newAbortController.signal,
      );
      yield;

      const alertRulesEnts = alertRules.map((rule) => {
        const mappedRule = mapAlertRule(rule);

        return new AlertRule(mappedRule, this.userData, rule.id);
      });

      this.alertRules = alertRulesEnts;
      this.alertRulesMap.clear();
      this.alertRulesMap.merge(alertRulesEnts.map((rule) => [rule.entityId, rule]));
      yield this.loadNotificationsSettings(alertRulesEnts);
    } catch (e) {
      yield;

      if (axios.isCancel(e)) {
        return;
      }
      this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.fetchAlertRules');
      console.error(e);
    } finally {
      yield;
      this.loading = false;
      this.abortControllers.delete('fetchAlertRules');
    }
  }

  @computed
  get rows(): AlertRule[] {
    return this.alertRules;
  }

  @computed
  get hasUnsavedChanges(): boolean {
    return this.alertRules.some((row) => row.isChanged || row.id == null) || this.deletedAlertRulesIds.length > 0;
  }

  @action.bound
  async saveAlertRules() {
    if (this.validationService.validateAlertRules(this.alertRules)) {
      return Promise.reject(new Error('Validation errors'));
    }

    try {
      if (this.wellbores.selected == null) {
        return;
      }
      const alertRules = this.serializeAlertRules(
        this.alertRules.filter((rule) => rule.isChanged || rule.id == null),
        this.wellbores.selected.id,
      );

      const createRules: TRawAlertRulesRequestDto[] = [];
      const updateRules: TRawAlertRulesRequestDto[] = [];
      const deleteRules: string[] = this.deletedAlertRulesIds;

      for (const rule of alertRules) {
        if (rule.id == null) {
          createRules.push(rule);
        } else {
          updateRules.push(rule);
        }
      }

      await this.api.saveAlertRules({
        create: createRules,
        update: updateRules,
        delete: deleteRules,
      });

      runInAction(() => {
        this.deletedAlertRulesIds = [];
      });

      if (this.wellbores.selected) {
        return this.fetchAlertRules(this.wellbores.selected.id);
      }
    } catch (e) {
      this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.saveAlertRules');
      console.error(e);
    }
  }

  @action.bound
  addNewAlertRuleItems(initialRecordsData?: TData[]) {
    if (!initialRecordsData) {
      const newItem = new AlertRule(this.getDefaultAlertRuleData(), this.userData);
      this.alertRules.push(newItem);
      this.alertRulesMap.set(newItem.entityId, newItem);
      this.alertRulesEventEmitter.emit('newAlertRules', [newItem]);

      return;
    }

    if (!initialRecordsData.length) {
      return;
    }

    const newAlertRules: AlertRule[] = [];

    for (const initialData of initialRecordsData) {
      const newItem = new AlertRule(initialData ?? this.getDefaultAlertRuleData(), this.userData);

      this.alertRules.push(newItem);
      newAlertRules.push(newItem);
      this.alertRulesMap.set(newItem.entityId, newItem);
    }

    this.alertRulesEventEmitter.emit('newAlertRules', newAlertRules);
  }

  @action.bound
  addNewAlertRuleItemPatches(patches: Partial<TData>[]): void {
    this.addNewAlertRuleItems(patches.map((patch) => this.createAlertRuleDataFromPatch(patch)));
  }

  @action.bound
  updateAlertRuleItems(data: [string, TData][]): void {
    const updatedAlertRules: AlertRule[] = [];

    for (const [recordId, ruleData] of data) {
      const ruleItem = this.alertRulesMap.get(recordId);

      if (!ruleItem) {
        console.warn(`cannot find alert rule item with id ${recordId}`);
        continue;
      }

      ruleItem.updateData(ruleData);
      updatedAlertRules.push(ruleItem);
    }

    this.alertRulesEventEmitter.emit('updateAlertRules', updatedAlertRules);
  }

  @action.bound
  applyAlertRuleItemPatches(data: [string, Partial<TData>][]): void {
    const updatedAlertRules: AlertRule[] = [];

    for (const [recordId, patch] of data) {
      const ruleItem = this.alertRulesMap.get(recordId);

      if (!ruleItem) {
        console.warn(`cannot find alert rule item with id ${recordId}`);
        continue;
      }

      const normalizedPatch = this.normalizeAlertRulePatch(ruleItem.data, patch);
      ruleItem.updateData({ ...ruleItem.data, ...normalizedPatch });
      updatedAlertRules.push(ruleItem);
    }

    this.alertRulesEventEmitter.emit('updateAlertRules', updatedAlertRules);
    this.validationService.validateAlertRules(updatedAlertRules);
  }

  @action.bound
  deleteAlertRuleItems(ids: string[]) {
    const firstRuleItemToDeleteIndex = this.alertRules.findIndex(({ entityId }) => ids.includes(entityId));

    if (firstRuleItemToDeleteIndex < 0) {
      return;
    }

    const idsMap = new Map(ids.map((id) => [id, id]));

    for (let i = 0; i < this.alertRules.length; ) {
      const rule = this.alertRules[i];

      if (idsMap.has(rule.entityId)) {
        this.alertRulesMap.delete(rule.entityId);
        this.alertRules.splice(i, 1);

        if (rule.id != null) {
          this.deletedAlertRulesIds.push(rule.id);
        }

        continue;
      }

      i++;
    }

    this.alertRulesEventEmitter.emit('deleteAlertRules', ids);
  }

  @action.bound
  onAlertRuleItemValueChange(recordId: string, field: keyof TAlertRule, value: TAlertRule[keyof TAlertRule]): void {
    const alertRuleItem = this.alertRulesMap.get(recordId);

    if (!alertRuleItem) {
      console.error(`cannot find corresponding item with id ${recordId}`);
      return;
    }

    const patch = { [field]: value } as Partial<TData>;
    this.applyAlertRuleItemPatches([[recordId, patch]]);
  }

  @action.bound
  setIsTemplatesDrawerOpen(is: boolean): void {
    this.isTemplatesDrawerOpen = is;
  }

  @flow.bound
  *setWellbore(wellbore: Wellbore) {
    this.wellbores.setSelected(wellbore);
    yield Promise.all([this.fetchLogs(), this.fetchBaseParameters(wellbore.id), this.fetchAlertRules(wellbore.id)]);
  }

  setWellboreById(wellboreId: number) {
    const wellbore = this.wellbores.list.find((wellbore) => wellbore.id === wellboreId);
    if (wellbore) {
      return this.setWellbore(wellbore);
    }
  }

  @action.bound
  setEditing(is: boolean): void {
    this.uiState.setEditing(is);
  }

  @flow.bound
  *discardChanges() {
    runInAction(() => {
      this.deletedAlertRulesIds = [];
    });

    const selectedWellbore = this.wellbores.selected;
    if (!selectedWellbore) {
      return;
    }

    yield this.fetchAlertRules(selectedWellbore.id);
  }

  mapTemplateRuleToAlertRule(template: TAlertRuleTemplateData): AlertRule {
    return new AlertRule(
      {
        ...template,
      },
      this.userData,
    );
  }

  @action.bound
  addAlertRules(alertRules: AlertRule[]): void {
    this.alertRules.push(...alertRules);
    alertRules.forEach((alertRule) => {
      this.alertRulesMap.set(alertRule.entityId, alertRule);
    });

    this.alertRulesEventEmitter.emit('newAlertRules', alertRules);
  }

  addFromTemplate(template: TAlertRulesTemplateData): void {
    this.addAlertRules(template.data.map((alertRule) => this.mapTemplateRuleToAlertRule(alertRule)));
  }

  saveTemplate(
    indexType: 'time' | 'depth',
    templateName: string,
    templateDescription: string,
    alertRules: AlertRule[],
  ) {
    const template = this.alertRulesTemplatesManager.templateFromList(
      alertRules.map((alertRule) => alertRule.data as TAlertRule),
      indexType,
      templateName,
      templateDescription,
    );
    return this.alertRulesTemplatesManager.createTemplate(template);
  }

  private async loadNotificationsSettings(alertRules: AlertRule[]) {
    const alertRuleIds = alertRules.map((alertRule) => alertRule.id).filter((id): id is string => id != null);
    if (!alertRuleIds.length) {
      return;
    }

    let notifications: TNotificationStatus[];

    try {
      notifications = await this.notificationsApi.fetchNotifications(alertRuleIds);
    } catch (e) {
      this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.fetchAlertRulesNotifications');
      console.error(e);
      return;
    }

    const notificationsMap = new Map(notifications.map((notification) => [notification.alertRuleId, notification]));
    for (const alertRule of alertRules) {
      if (alertRule.id) {
        alertRule.setNotificationsEnabled(notificationsMap.get(alertRule.id)?.shown ?? true);
      }
    }
  }

  @action.bound
  setAlertRuleNotificationsOption(alertRuleId: string, shown: boolean) {
    const alertRule = this.alertRulesMap.get(alertRuleId);
    if (!alertRule || !alertRule.id) {
      return;
    }
    const previousValue = alertRule.notificationsEnabled;
    alertRule.setNotificationsEnabled(shown);
    this.updateAlertRuleItems([[alertRuleId, alertRule.data]]);
    this.notificationsApi.setAlertRuleNotificationsOption(alertRule.id, shown).catch((e) => {
      runInAction(() => {
        alertRule.setNotificationsEnabled(previousValue);
      });
      this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.saveAlertRuleNotificationsOption');
      console.error(e);
    });
  }

  @action.bound
  setAllAlertRulesNotificationsOption(shown: boolean) {
    const targets = this.alertRules.filter((rule) => rule.id != null && rule.notificationsEnabled !== shown);
    if (targets.length === 0) {
      return;
    }

    const previousValues = new Map(targets.map((rule) => [rule.entityId, rule.notificationsEnabled]));

    for (const rule of targets) {
      rule.setNotificationsEnabled(shown);
    }
    this.updateAlertRuleItems(targets.map((rule) => [rule.entityId, rule.data]));

    Promise.all(
      targets.map((rule) =>
        this.notificationsApi.setAlertRuleNotificationsOption(rule.id!, shown).then(
          () => ({ ok: true as const, rule }),
          (error: unknown) => ({ ok: false as const, rule, error }),
        ),
      ),
    ).then((results) => {
      const failed = results.filter((result) => !result.ok);
      if (failed.length === 0) {
        return;
      }

      runInAction(() => {
        for (const { rule } of failed) {
          rule.setNotificationsEnabled(previousValues.get(rule.entityId)!);
        }
        this.updateAlertRuleItems(failed.map(({ rule }) => [rule.entityId, rule.data]));
      });
      this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.saveAlertRuleNotificationsOption');
      failed.forEach(({ error }) => console.error(error));
    });
  }

  @action.bound
  setAllJournalEnabled(enabled: boolean) {
    const patches: [string, Partial<TData>][] = this.alertRules
      .filter((rule) => rule.data.journalEnabled !== enabled)
      .map((rule) => [rule.entityId, { journalEnabled: enabled }]);

    if (patches.length === 0) {
      return;
    }

    this.applyAlertRuleItemPatches(patches);
  }

  @flow.bound
  *fetchBaseParameters(wellboreId: number) {
    try {
      const params: BaseParameter[] = yield this.wellDirectoryService.getBaseParameters(
        wellboreId,
        this.index === 'time' ? 'LOG_TIME' : 'LOG_DEPTH',
      );

      this.baseParameters = params;
      return params;
    } catch (e) {
      this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.fetchBaseParameters');
      console.error(e);
      return [];
    }
  }

  async loadInitialData() {
    await Promise.all([
      this.wellbores.fetchWellbores(this.wellId),
      this.fetchDirectories(),
      this.alertRulesTemplatesManager.getAll(),
    ]);
    const wellbore = this.wellbores.getDefaultSelected(false);
    if (wellbore == null) {
      return;
    }
    return this.setWellbore(wellbore);
  }

  @flow.bound
  private async *fetchDirectories() {
    try {
      const [
        anomalyTypes,
        anomalySubtypes,
        parameterTypes,
        workClassifierStageTypes,
        wellboreTypes,
        trajectoryTypes,
        sectionTypes,
        stratUnitTypes,
        reservoirTypes,
        runTypes,
        unitsList,
        classOfUnits,
        autosupervisorOperations,
        autosupervisorKPIstates,
      ] = await this.directoryService.loadObjects([
        'Common_AnomalyTypes',
        'Common_AnomalySubTypes',
        'Common_Parameter',
        'GOreport_WorkClassifier_Stage',
        'Common_WellboreType',
        'Common_TrajectoryType',
        'Common_SectionType',
        'Common_StratUnitType',
        'Common_Reservoir',
        'Common_RunType',
        'Common_Uom',
        'Common_ClassOfUnit',
        'Common_AutosupervisorOperations',
        'Common_AutosupervisorKPIstates',
      ]);
      yield;

      this.anomalyTypes = anomalyTypes ?? [];
      this.anomalySubtypes = anomalySubtypes ?? [];
      this.parameterTypes = parameterTypes ?? [];
      this.workClassifierStageTypes = workClassifierStageTypes ?? [];
      this.wellboreTypes = wellboreTypes ?? [];
      this.trajectoryTypes = trajectoryTypes ?? [];
      this.sectionTypes = sectionTypes ?? [];
      this.stratUnitTypes = stratUnitTypes ?? [];
      this.reservoirTypes = reservoirTypes ?? [];
      this.runTypes = runTypes ?? [];
      this.unitsList = unitsList ?? [];
      this.classOfUnits = classOfUnits ?? [];
      this.autosupervisorOperations = autosupervisorOperations ?? [];
      this.autosupervisorKPIstates = autosupervisorKPIstates ?? [];

      this.areDirectoriesLoaded = true;
    } catch (e) {
      yield;
      console.error(e);
    }
  }

  @flow.bound
  private async *fetchLogs() {
    try {
      if (this.wellbores.selected == null) {
        return;
      }
      const logs = await this.wellDirectoryService.getLogs(
        this.wellId,
        this.wellbores.selected.id,
        this.index === 'time' ? 'LOG_TIME' : 'LOG_DEPTH',
      );
      yield;
      this.wellboreLogs = logs;
    } catch (e) {
      yield;
      this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.fetchLogs');
      console.error(e);
    }
  }

  init = (): VoidFunction => {
    this.loadInitialData();

    return () => {
      this.abortControllers.forEach((abC) => abC.abort());
      this.wellbores.clear();
    };
  };
}
